import Vue from 'vue'
import Vuex from 'vuex'
import Organization from '@/services/organizations'
import actions from '@/store/actions'
import * as Cookies from 'js-cookie'

Vue.use(Vuex)

const STORAGE_HASH = 'adf238fna'
export const STORAGE_KEY = `canary-${STORAGE_HASH}`

const checkCurrentLangForUnsupportedLang = () => {
  const currentLang = window.localStorage.getItem('currentLanguage')

  if (currentLang && currentLang == 'en_US') {
    window.localStorage.setItem('currentLanguage', 'en')
    return 'en'
  }

  if (currentLang && currentLang == 'es_ES') {
    window.localStorage.setItem('currentLanguage', 'en')
    return 'es'
  }
}

const state = {
  user: null,
  token: window.localStorage.getItem('token'),
  // This should mirror and be kept in sync with this.$language.current
  // and the current user's language preference as saved in the DB
  currentLanguage: checkCurrentLangForUnsupportedLang() || 'en',
  // This default organization is intended to be a suitable fallback for
  // testing purposes and staff users, who may not have an organization
  // and subdomain.
  organizationDetails: Organization.create({
    name: 'Your Organization',
  }),
  hasCompletedFinHealthSurveyStart: false,
  hasCompletedHouseholdSurveyStart: false,
  reproductiveRightsFlow: {
    steps: [
      {
        show: '/pay-pal-verify',
        next: '/rights-events',
      },
      {
        show: '/rights-events',
        next: 'rights-details',
      },
      {
        show: '/rights-details',
        next: '/rights-documentation',
      },
      {
        show: '/rights-documentation',
        next: '/rights-summary',
      },
    ],
  },
  defaultRequestFlow: {
    steps: [
      {
        show: '/new-request',
        next: 'pickRouteFromStart',
      },
      {
        show: '/request-household-details',
        next: '/request-financial-health',
      },
      {
        show: '/request-financial-health',
        next: '/pay-pal-verify',
      },
      {
        show: '/pay-pal-verify',
        next: '/event-categories',
      },
      {
        show: '/event-categories',
        next: 'pickRouteFromEventCategories',
      },
      {
        show: '/create-events',
        next: 'pickRouteFromEvents',
      },
      {
        show: '/event-documentation',
        next: 'pickRouteFromEventDocumentation',
      },
      {
        show: '/expense-categories',
        next: 'pickRouteFromExpenseCategories',
      },
      {
        show: '/expense-documentation',
        next: 'pickRouteFromExpenseDocumentation',
      },
    ],
  },
  /*********************
   * TCR Nominee Flows *
   *********************/

  // 1st month flow
  nomineeMonthOneFlow: {
    steps: [
      {
        show: '/nominee',
        next: '/impact-statement',
      },
      {
        show: '/impact-statement',
        next: '/personal-details',
      },
      {
        show: '/personal-details',
        next: '/documentation',
      },
      {
        show: '/documentation',
        next: '/payment-details',
      },
      {
        show: '/payment-details',
        next: '/nominee',
      },
    ],
  },

  // 2nd through 11th month(s) flow
  nomineeMonthsTwoThroughElevenFlow: {
    steps: [
      {
        show: '/nominee',
        next: '/stipend-request/goal-progression',
      },
      {
        show: '/stipend-request/goal-progression',
        next: '/stipend-request/documentation',
      },
      {
        show: '/stipend-request/documentation',
        next: '/stipend-request/submitted',
      },
      {
        show: '/stipend-request/submitted',
        next: '/nominee',
      },
    ],
  },

  // 12th month flow
  nomineeMonthTwelveFlow: {
    steps: [
      {
        show: '/nominee',
        next: '/stipend-request/goal-progression',
      },
      {
        show: '/stipend-request/goal-progression',
        next: '/stipend-request/program-exit',
      },
      {
        show: '/stipend-request/program-exit',
        next: '/stipend-request/documentation',
      },
      {
        show: '/stipend-request/documentation',
        next: '/stipend-request/submitted',
      },
      {
        show: '/stipend-request/submitted',
        next: '/nominee',
      },
    ],
  },

  organizationCssVars: null,
  currentRequest: JSON.parse(window.localStorage.getItem('currentRequest')),
  currentDonation: JSON.parse(window.localStorage.getItem('currentDonation')),
  currentAnonymousDonor: JSON.parse(window.localStorage.getItem('currentAnonymousDonor')),
  currentPayrollRequest: JSON.parse(window.localStorage.getItem('currentPayrollRequest')),
  requestHasUncommonExpenseCategories: null,
  requestHasCommonExpenseCategories: null,
  requestHasUncommonEventCategories: null,
}

const mutations = {
  updateEvent(state, payload) {
    const requestEventIndex = state.currentRequest.eventsRef.findIndex(
      event => event.id == payload.eventRef.id,
    )
    state.currentRequest.eventsRef.splice(requestEventIndex, 1, payload.eventRef)
  },
  updateUncommonExpenseCheck(state, payload) {
    state.requestHasUncommonExpenseCategories = payload
  },
  updateCommonExpenseCheck(state, payload) {
    state.requestHasCommonExpenseCategories = payload
  },
  updateUncommonEventCategories(state, payload) {
    state.requestHasUncommonEventCategories = payload
  },
  UPDATE_ORG: (state, payload) => {
    state.organizationDetails = payload
  },
  UPDATE_USER: (state, payload) => {
    state.user = payload
  },

  UPDATE_HAS_COMPLETED_FIN_HEALTH_SURVEY: (state, payload) => {
    state.hasCompletedFinHealthSurveyStart = payload
  },
  UPDATE_HAS_COMPLETED_HOUSEHOLD_SURVEY: (state, payload) => {
    state.hasCompletedHouseholdSurveyStart = payload
  },
  UPDATE_USERTOKEN: (state, payload) => {
    state.token = payload
    if ((payload && typeof payload === 'string') || payload instanceof String) {
      window.localStorage.setItem('token', payload)
      return
    }
  },
  UPDATE_CURRENT_LANGUAGE(state, value) {
    // Need to keep the vm.$language in sync
    if (this.$language) {
      this.$language.current = value
    }
    state.currentLanguage = value
    if (window.localStorage.currentLanguage === 'null') {
      window.localStorage.setItem('currentLanguage', value || 'en')
    }
    if ((value && typeof value === 'string') || value instanceof String) {
      window.localStorage.setItem('currentLanguage', value)
    }
  },
  // Reset state to defaults
  RESET_STATE(state) {
    state.token = null
    state.user = null
    state.currentRequest = null
    state.currentDonation = null
    state.currentAnonymousDonor = null
    state.currentPayrollRequest = null
    Cookies.remove('sessionId', { path: '/' })
    window.localStorage.clear()
  },
  UPDATE_CURRENT_REQUEST: (state, payload) => {
    state.currentRequest = payload
    window.localStorage.setItem('currentRequest', JSON.stringify(payload))
  },
  UPDATE_CURRENT_DONATION: (state, payload) => {
    state.currentDonation = payload
    window.localStorage.setItem('currentDonation', JSON.stringify(payload))
  },
  CLEAR_CURRENT_DONATION(state) {
    state.currentDonation = null
    window.localStorage.removeItem('currentDonation')
  },
  UPDATE_CURRENT_ANONYMOUS_DONOR: (state, payload) => {
    state.currentAnonymousDonor = payload
    window.localStorage.setItem('currentAnonymousDonor', JSON.stringify(payload))
  },
  CLEAR_CURRENT_ANONYMOUS_DONOR(state) {
    state.currentAnonymousDonor = null
    window.localStorage.removeItem('currentAnonymousDonor')
  },
  UPDATE_CURRENT_PAYROLL_REQUEST: (state, payload) => {
    state.currentPayrollRequest = payload
    window.localStorage.setItem('currentPayrollRequest', JSON.stringify(payload))
  },

  CLEAR_CURRENT_PAYROLL_REQUEST(state) {
    state.currentPayrollRequest = null
    window.localStorage.removeItem('currentPayrollRequest')
  },
}

const plugins = []

const getters = {
  userIsLoggedIn: state => {
    if (state.token && !state.user) {
      //attempt to reset the user if we have a token
    }
    return state.token && state.user
  },
  organizationCssVars: state => ({
    '--dark-brand-color': `#${state.organizationDetails.darkBrandColor}`,
    '--light-brand-color': `#${state.organizationDetails.lightBrandColor}`,

    // Employees can only see a mobile view
    // Admin and staff users see a desktop view
    '--maximum-width': state.user && (state.user.isAdmin || state.user.isStaff) ? '100%' : '500px',
  }),
  getEventFromRequest: (state, eventId) => {
    return state.currentRequest.eventsRef.find(event => event.id == eventId)
  },
}

export default new Vuex.Store({
  state,
  mutations,
  actions,
  getters,
  plugins,
})
